/**
 * Shared React Query hook for fetching a tenant's custom field definitions for
 * an entity, used by the generic CustomFieldsSection renderer.
 *
 * The query key matches the admin module's hook so the definition list is shared
 * in the cache and a definition edit there refreshes consumers here.
 * See docs/platform/custom-fields-design.md.
 */
import { useQuery } from '@tanstack/react-query';

import { listDefinitionsApiV1CustomFieldsDefinitionsGet } from '../../../generated/api/custom-fields/custom-fields';
import { allCustomFieldDefinitionsKey } from './customFieldUtils';
import type { CustomFieldDefinition } from '../../../generated/api/models/customFieldDefinition';

export const customFieldDefinitionsKey = (entityType: string) =>
  ['custom-fields', 'definitions', entityType] as const;

export function useCustomFieldDefinitions(entityType: string | undefined) {
  return useQuery({
    queryKey: customFieldDefinitionsKey(entityType ?? ''),
    queryFn: () =>
      listDefinitionsApiV1CustomFieldsDefinitionsGet({ entity_type: entityType }),
    enabled: !!entityType,
  });
}

// Re-exported from `customFieldUtils`, which is where it is DEFINED — see the
// note there on why the key cannot live beside its hook.
export { allCustomFieldDefinitionsKey };

/**
 * Every definition the tenant has, for every record type, in one request.
 *
 * `GET /custom-fields/definitions` takes an OPTIONAL `entity_type`; omitting it
 * returns the lot, and the route is `require_internal_read` — not `require_admin`,
 * which only guards the `/entities` picker. That distinction is what lets a
 * surface mounted for a per-module admin (who is not a tenant admin) state which
 * record types a relationship actually bears fields on, instead of degrading to
 * silence on one of its two hosts.
 *
 * Each row carries its own `entity_type`, so callers group client-side — see
 * `relationshipScopeUsage`.
 */
export function useAllCustomFieldDefinitions() {
  return useQuery({
    queryKey: allCustomFieldDefinitionsKey,
    queryFn: () => listDefinitionsApiV1CustomFieldsDefinitionsGet(),
  });
}

export type { CustomFieldDefinition };
